import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Button, Modal, ProgressBar, Spinner } from '@themesberg/react-bootstrap'
import { calculateReward } from '../../../../../controller/api/__loto__/result'
import { useCookies } from 'react-cookie'
import { useHistory } from 'react-router-dom'

export default function CalculateRewardPopup(props) {
  const { show, onHide, resultId, period } = props

  const [remainingTicket, setRemainingTicket] = useState(0)
  const [initialTotalTicket, setInitialTotalTicket] = useState(0)
  const [done, setDone] = useState(false)
  const [isRunning, setIsRunning] = useState(false)
  const [statusText, setStatusText] = useState('Đang chuẩn bị dữ liệu')
  const [errorMessage, setErrorMessage] = useState('')
  const totalRewardTicket = useRef(0)
  const totalUnrewardTicket = useRef(0)
  const totalReward = useRef(0)
  const hasStarted = useRef(false)
  const requestInFlight = useRef(false)

  const [cookies] = useCookies(['lottmekong'])
  const history = useHistory()

  const processedTicket = totalRewardTicket.current + totalUnrewardTicket.current
  const displayTotalTicket = processedTicket + remainingTicket
  const progressPercent = useMemo(() => {
    if (displayTotalTicket <= 0) {
      return 0
    }

    return Math.min(100, Math.round((processedTicket / displayTotalTicket) * 100))
  }, [displayTotalTicket, processedTicket])

  const resetState = useCallback(() => {
    totalRewardTicket.current = 0
    totalUnrewardTicket.current = 0
    totalReward.current = 0
    setRemainingTicket(0)
    setInitialTotalTicket(0)
    setDone(false)
    setIsRunning(false)
    setErrorMessage('')
    setStatusText('Đang chuẩn bị dữ liệu')
    hasStarted.current = false
    requestInFlight.current = false
  }, [])

  const markFatalError = useCallback((message) => {
    setErrorMessage(message || 'Không thể tính thưởng. Vui lòng thử lại.')
    setDone(true)
    setIsRunning(false)
    setStatusText('Tính thưởng thất bại')
  }, [])

  const _onCalculating = useCallback(async () => {
    if (requestInFlight.current || !resultId) {
      return
    }

    requestInFlight.current = true
    setIsRunning(true)
    setStatusText(processedTicket === 0 ? 'Đang dò vé đợt đầu' : 'Đang tiếp tục tính thưởng')

    try {
      const response = await calculateReward(cookies.lottmekong, 10, resultId)

      if (!response) {
        markFatalError()
        return
      }

      const rewardTicket = Number(response.totalRewardTicket || 0)
      const unrewardTicket = Number(response.totalUnrewardTicket || 0)
      const rewardValue = Number(response.totalReward || 0)
      const newRemainingTicket = Number(response.totalTicket)

      if (![rewardTicket, unrewardTicket, rewardValue, newRemainingTicket].every(Number.isFinite)) {
        markFatalError(response.message)
        return
      }

      totalRewardTicket.current += rewardTicket
      totalUnrewardTicket.current += unrewardTicket
      totalReward.current += rewardValue

      setRemainingTicket(newRemainingTicket)
      setInitialTotalTicket((currentTotal) => {
        if (currentTotal > 0) {
          return currentTotal
        }

        return processedTicket + newRemainingTicket + rewardTicket + unrewardTicket
      })

      if (newRemainingTicket === 0) {
        setDone(true)
        setIsRunning(false)
        setStatusText(processedTicket + rewardTicket + unrewardTicket > 0
          ? 'Đã tính thưởng xong'
          : 'Không có vé mới để tính thưởng')
        return
      }

      setIsRunning(false)
    } catch (error) {
      markFatalError()
    } finally {
      requestInFlight.current = false
    }
  }, [cookies.lottmekong, markFatalError, processedTicket, resultId])

  useEffect(() => {
    if (!show) {
      resetState()
      return
    }

    if (!resultId) {
      return
    }

    if (!hasStarted.current) {
      hasStarted.current = true
      _onCalculating()
    }
  }, [_onCalculating, resetState, resultId, show])

  useEffect(() => {
    if (!show || done || isRunning || errorMessage || !hasStarted.current) {
      return
    }

    if (remainingTicket > 0) {
      _onCalculating()
    }
  }, [_onCalculating, done, errorMessage, isRunning, remainingTicket, show])

  const closeModal = useCallback((callback) => {
    onHide(totalRewardTicket.current, totalUnrewardTicket.current, async () => {
      resetState()

      if (callback) {
        callback()
        return
      }
    })
  }, [onHide, resetState])

  const _onHide = () => {
    if (!done) {
      alert('Chưa tính thưởng xong')
      return
    }

    closeModal()
  }

  const onViewCalculatedTickets = () => {
    if (!done || errorMessage) {
      return
    }

    closeModal(() => {
      history.push(`/loto/statistic-by-ticket?resultId="${resultId}"`)
    })
  }

  return (
    <Modal
      show={show}
      size="lg"
      aria-labelledby="contained-modal-title-vcenter"
      centered
      backdrop="static"
      onHide={_onHide}
    >
      <Modal.Header closeButton>
        <Modal.Title id="contained-modal-title-vcenter">
          Tính thưởng kỳ #{period ? period.name : resultId}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex align-items-center mb-3">
          {isRunning
            ? <Spinner animation="border" variant="primary" className="me-3" />
            : <div className="me-3" style={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: errorMessage ? '#dc3545' : done ? '#2ca58d' : '#d1d5db' }} />
          }
          <div>
            <div className="fw-bold">{statusText}</div>
            <div className="text-muted small">
              {errorMessage
                ? errorMessage
                : done
                  ? 'Bạn có thể đóng popup hoặc đi thẳng sang trang chi tiết.'
                  : 'Hệ thống đang xử lý theo từng đợt 10 vé.'}
            </div>
          </div>
        </div>

        <ProgressBar
          className="progress-md mb-3"
          variant={errorMessage ? 'danger' : done ? 'success' : 'info'}
          animated={isRunning}
          now={progressPercent}
          min={0}
          max={100}
          label={`${progressPercent}%`}
        />

        <div className="row g-3 mb-2">
          <div className="col-4">
            <div className="border rounded p-3 text-center h-100">
              <div className="text-muted small">Đã xử lý</div>
              <div className="fw-bold fs-5">{processedTicket} / {displayTotalTicket || initialTotalTicket || 0}</div>
            </div>
          </div>
          <div className="col-4">
            <div className="border rounded p-3 text-center h-100">
              <div className="text-muted small">Vé trúng</div>
              <div className="fw-bold fs-5 text-success">{totalRewardTicket.current}</div>
            </div>
          </div>
          <div className="col-4">
            <div className="border rounded p-3 text-center h-100">
              <div className="text-muted small">Không trúng</div>
              <div className="fw-bold fs-5 text-danger">{totalUnrewardTicket.current}</div>
            </div>
          </div>
        </div>

        <div className="text-muted small">
          Còn lại: <b>{remainingTicket}</b>
        </div>
      </Modal.Body>
      <Modal.Footer>
        {done && processedTicket > 0 && !errorMessage &&
          <Button variant="warning" onClick={onViewCalculatedTickets}>
            Xem vé đã tính
          </Button>
        }
        <Button variant={done && !errorMessage ? "success" : "danger"} onClick={_onHide}>
          {done && !errorMessage ? 'Hoàn tất' : 'Đang xử lý'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
